import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Collapse,
} from "shards-react";

import { Tooltip } from "react-tippy";
import SmoothScroll from "./smooth-scroll.polyfills.min";

import USMap from "./../components/common/USMap";
import PageTitle from "./../components/common/PageTitle";
import SmallStats from "./../components/common/SmallStats";
import UsersOverview from "./../components/blog/UsersOverview";
import * as utils from "../utils/utils";

const scroll = new SmoothScroll();

// const Z_URL = process.env.REACT_APP_DEVMODE ? "http://localhost:1337" : "https://infection2020.netlify.app/z.json"
const Z_URL = "https://infection2020.netlify.app/z.json";

let globalArcCountyData = null;

function polishValue(value) {
  return value.trimLeft();
}

function polishCountyName(value) {
  // remove quotes
  value = value.replace(/"/g, "");
  value = value.trim();
  return value;
}

function processCountyName(str) {
  // const state = getState(name)
  const chunks = str.split(",");
  if (chunks.length > 1) {
    const state = polishCountyName(chunks[1]);
    const countyChunk = polishCountyName(chunks[0]);
    // remove 'County' case-insensitive from the string
    const county = countyChunk.replace(/ County/gi, "");
    return { county: county, state: state };
  } else {
    return { county: polishCountyName(str), state: "Other" };
  }
}

// ignore entries
function getInfoFromHistoricalEntry(entry) {
  // const cityAndState = polishValue(entry[0])
  const { county, state } = processCountyName(entry[0]);
  return {
    countyAndState: `${county} County, ${state}`,
    county: county,
    state: state,
    country: entry[1],
    lat: entry[2],
    lng: entry[3],
  };
}

function getDataByDate(historicalData) {
  const header = historicalData[0];
  const dateToData = {};
  for (let dateIndex = 4; dateIndex < header.length; dateIndex++) {
    const date = header[dateIndex].trim();
    const totalCount = 0;
    const stateToCount = {};
    const cityToCount = {};
    for (let cityIndex = 1; cityIndex < historicalData.length; cityIndex++) {
      const entry = historicalData[cityIndex];

      const value = parseInt(entry[dateIndex]);
      if (isNaN(value)) {
        // Find previous valid value
        for (
          let reverseDateIndex = dateIndex - 1;
          reverseDateIndex >= 4;
          reverseDateIndex--
        ) {
          value = parseInt(entry[reverseDateIndex]);
          if (!isNaN(value)) {
            break;
          }
        }
        if (isNaN(value)) {
          value = 0;
        }
      }

      const { countyAndState, state } = getInfoFromHistoricalEntry(entry);

      totalCount += value;

      let stateValue = stateToCount[state];
      if (stateValue == undefined) {
        stateToCount[state] = value;
      } else {
        stateToCount[state] += value;
      }

      let cityValue = cityToCount[countyAndState];
      if (cityValue == undefined) {
        cityToCount[countyAndState] = value;
      } else {
        cityToCount[countyAndState] += value;
      }
    }

    dateToData[date] = {
      totalCount: totalCount,
      stateToCount: stateToCount,
      cityToCount: cityToCount,
    };
  }
  return dateToData;
}

function isElementInViewport(el) {
  if (!el) return false;
  var rect = el.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight ||
        document.documentElement.clientHeight) /* or $(window).height() */ &&
    rect.right <=
      (window.innerWidth ||
        document.documentElement.clientWidth) /* or $(window).width() */
  );
}

// Sorts entries, oldest date first.
function sortByDate(dateToData) {
  return Object.entries(dateToData).sort((a, b) => {
    const date1 = a[0];
    const date2 = b[0];
    return Date.parse(date1) - Date.parse(date2);
  });
}

function sortCountyToData(countyToData) {
  return Object.entries(countyToData).sort((entry1, entry2) => {
    const confirmed1 = entry1[1].countyConfirmed;
    const confirmed2 = entry2[1].countyConfirmed;
    return confirmed2 - confirmed1;
  });
}

function sortData(stateToCountyToData, userCounty) {
  let userState = userCounty && userCounty.state;
  let userStateEntry = null;

  // Get counties sorted first
  let stateEntries = Object.entries(stateToCountyToData).map(
    ([state, { stateConfirmed, stateDeaths, stateNew, countyToData }]) => {
      const stateListData = {
        stateConfirmed,
        stateDeaths,
        stateNew,
        sortedCountyEntries: sortCountyToData(countyToData),
      };
      const stateEntry = [state, stateListData];
      if (userState == state) {
        userStateEntry = stateEntry;
        return null;
      }
      return [state, stateListData];
    }
  );

  if (userStateEntry) {
    // filter out null/undefined
    stateEntries = stateEntries.filter((e) => e);
  }

  // Now sort states
  const sortedStateEntries = stateEntries.sort((entry1, entry2) => {
    const stateConfirmed1 = entry1[1].stateConfirmed;
    const stateConfirmed2 = entry2[1].stateConfirmed;
    return stateConfirmed2 - stateConfirmed1;
  });

  if (userStateEntry) {
    // List user's state first
    return [userStateEntry, ...sortedStateEntries];
  } else {
    return sortedStateEntries;
  }
}

function calcCFR(totalConfirmed, totalDeaths) {
  if (totalConfirmed && totalDeaths) {
    return (totalDeaths / totalConfirmed) * 100;
  } else {
    return null;
  }
}

// TREEHOUSE
const BlogOverview = ({ smallStats }) => {
  const [updateEpoch, setUpdateEpoch] = useState(null);
  const [stateToCountyToData, setStateToCountyToData] = useState(null);
  const [sortedStateEntries, setSortedStateEntries] = useState(null);
  const [totalConfirmed, setTotalConfirmed] = useState(0);
  const [totalRecovered, setTotalRecovered] = useState(0);
  const [totalDeaths, setTotalDeaths] = useState(0);
  const [userCounty, setUserCounty] = useState(null);
  const [diff, setDiff] = useState(null);
  const [ts, setTs] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [alreadyDonated, setAlreadyDonated] = useState(false);

  // Toggle visibility
  const [showCityStats, setShowCityStats] = useState(true);
  const [showState, setShowState] = useState({});

  // Map state
  let sortedStateEntriesToShow = sortedStateEntries;
  if (userCounty) {
    sortedStateEntriesToShow = sortData(stateToCountyToData, userCounty);
    // set the default show behavior to visible
    if (showState[userCounty.state] == undefined) {
      // no need to setState
      showState[userCounty.state] = true;
    }
  }

  // Get donation state
  let alreadyDonatedToUse = alreadyDonated;
  const cookie = utils.cookies.get(utils.DONATE_COOKIE_NAME);
  if (cookie && cookie.alreadyDonated) {
    console.log("Already donated cookie found!", cookie);
    alreadyDonatedToUse = true;
  }

  function setAlreadyDonatedAndCookie() {
    utils.cookies.set(
      utils.DONATE_COOKIE_NAME,
      {
        alreadyDonated: true,
      },
      {
        path: "/",
        maxAge: 2147483647,
      }
    );
    setAlreadyDonated(true);
  }

  // Small stats state
  const confirmedChart = createConfirmedChart(totalConfirmed, diff, ts);
  const deathsChart = createDeathsChart(totalDeaths, diff, ts);
  const recoveredChart = createRecoveredChart(totalRecovered, diff, ts);
  const cfrChart = createCFRChart(
    calcCFR(totalConfirmed, totalDeaths),
    diff,
    ts
  );

  function toggleShowState(state, isShow) {
    const showObj = {};
    if (isShow != undefined) {
      showObj[state] = isShow;
    } else {
      showObj[state] = !showState[state];
    }
    setShowState({
      ...showState,
      ...showObj,
    });
  }

  function setBigChartLocation(state, county) {
    const bigChartEl = document.querySelector(".big-chart-container");

    if (!isElementInViewport(bigChartEl)) {
      scroll.animateScroll(bigChartEl, null, {
        speed: 1000,
        easing: "easeOutQuad",
      });
    }

    setSelectedState(state);
    setSelectedCounty(county);
  }

  function isShowState(state) {
    return showState[state];
  }

  async function fetchData() {
    let _totalConfirmed = 0;
    let _totalRecovered = 0;
    let _totalDeaths = 0;

    // get Z
    try {
      const resp = await fetch(Z_URL);
      const {
        stateToCountyToData,
        totalConfirmed,
        totalDeaths,
        totalRecovered,
        updateEpoch,
        diff,
        ts,
      } = await resp.json();
      if (totalConfirmed && totalConfirmed > _totalConfirmed) {
        _totalConfirmed = totalConfirmed;
      }
      if (totalDeaths && totalDeaths > _totalDeaths) {
        _totalDeaths = totalDeaths;
      }
      if (totalRecovered && totalRecovered > _totalRecovered) {
        _totalRecovered = totalRecovered;
      }
      setStateToCountyToData(stateToCountyToData);
      setSortedStateEntries(sortData(stateToCountyToData));
      setUpdateEpoch(updateEpoch);
      setTs(ts);
      if (diff) setDiff(diff);
    } catch (err) {
      console.error("Error fetching Z data: " + err);
    }

    if (_totalConfirmed > totalConfirmed) {
      setTotalConfirmed(_totalConfirmed);
    }
    if (_totalDeaths > totalDeaths) {
      setTotalDeaths(_totalDeaths);
    }
    if (_totalRecovered > totalRecovered) {
      setTotalRecovered(_totalRecovered);
    }
  }

  function parseAggData(data) {
    if (!data) return;
    const entries = data.features;
    if (!entries || entries.length < 1) return;

    let _totalConfirmed = 0;
    let _totalDeaths = 0;
    let _totalRecovered = 0;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const atts = entry.attributes;
      if (atts && atts.Country_Region == "US") {
        _totalConfirmed = atts.Confirmed;
        _totalDeaths = atts.Deaths;
        _totalRecovered = atts.Recovered;
        // const lastUpdateEpoch = atts.Last_Update;
        break;
      }
    }

    return { _totalConfirmed, _totalRecovered, _totalDeaths };
  }

  function parseCountyData(data) {
    if (!data) return;
    const entries = data.features;
    if (!entries || entries.length < 1) return;

    let _totalConfirmed = 0;
    let _totalRecovered = 0;
    let _totalDeaths = 0;

    const countyToData = {};
    entries.map((entry) => {
      const atts = entry.attributes;
      const country = polishValue(atts.Country_Region);
      const city = polishValue(atts.Province_State);
      const lat = atts.Lat;
      const lng = atts.Long_;
      const confirmed = atts.Confirmed;
      const deaths = parseInt(atts.Deaths);
      const recovered = atts.Recovered;
      const lastUpdateEpoch = atts.Last_Update;
      _totalConfirmed += confirmed;
      _totalRecovered += recovered;
      _totalDeaths += deaths;
      countyToData[city] = {
        lat,
        lng,
        confirmed,
        deaths,
        recovered,
        lastUpdateEpoch,
      };
    });

    globalArcCountyData = countyToData;

    return { _totalConfirmed, _totalRecovered, _totalDeaths };
  }

  useEffect(() => {
    // fetchUserGeo();
    fetchData();
  }, []); // provide empty array so we only execute this once (on mount) and not every update

  return (
    <Container fluid className="main-content-container custom-container">
      {/* Page Header */}
      <Row noGutters className="page-header pb-2">
        <div className="mb-1" id="google_translate_element"></div>
        <PageTitle
          title="United States Coronavirus (COVID-19) Tracker"
          subtitle="Dashboard"
          className="text-sm-left mb-3"
        />
      </Row>

      <USMap
        // markersData={markersData}
        data={stateToCountyToData}
        updateEpoch={updateEpoch}
        setUserCounty={setUserCounty}
        ts={ts}
      />

      <h5>Nationwide Live Data</h5>
      <p>
        Note: As of 4/17, the number of cases and deaths now include{" "}
        <b>probable cases</b> to match the{" "}
        <a
          href="https://www.cdc.gov/coronavirus/2019-ncov/cases-updates/cases-in-us.html"
          target="_blank"
        >
          new CDC guidelines
        </a>
        . This contributed to the spike in new deaths displayed for 4/17, which
        includes 4,226 probable deaths.
      </p>

      {/* Small Stats Blocks */}
      <Row>
        <Col className="col-lg small-stats-spacing" key={0} md="6" sm="6">
          <SmallStats
            id={`small-stats-0`}
            variation="1"
            chartData={confirmedChart.datasets}
            chartLabels={confirmedChart.chartLabels}
            label={confirmedChart.label}
            value={confirmedChart.value}
            valueDiff={confirmedChart.valueDiff}
            percentage={confirmedChart.percentage}
            increase={confirmedChart.increase}
            isIncreaseBad={confirmedChart.isIncreaseBad}
            decrease={confirmedChart.decrease}
            msg={confirmedChart.msg}
            timeAgo={confirmedChart.timeAgo}
          />
        </Col>
        <Col className="col-lg small-stats-spacing" key={1} md="6" sm="6">
          <SmallStats
            id={`small-stats-1`}
            variation="1"
            chartData={deathsChart.datasets}
            chartLabels={deathsChart.chartLabels}
            label={deathsChart.label}
            value={deathsChart.value}
            valueDiff={deathsChart.valueDiff}
            percentage={deathsChart.percentage}
            increase={deathsChart.increase}
            isIncreaseBad={deathsChart.isIncreaseBad}
            decrease={deathsChart.decrease}
            msg={deathsChart.msg}
            timeAgo={deathsChart.timeAgo}
          />
        </Col>
        <Col className="col-lg small-stats-spacing" key={3} md="6" sm="6">
          <SmallStats
            id={`small-stats-3`}
            variation="1"
            chartData={cfrChart.datasets}
            chartLabels={cfrChart.chartLabels}
            label={cfrChart.label}
            value={cfrChart.value ? `${cfrChart.value.toFixed(2)}%` : null}
            valueDiff={cfrChart.valueDiff}
            percentage={cfrChart.percentage}
            increase={cfrChart.increase}
            isIncreaseBad={cfrChart.isIncreaseBad}
            decrease={cfrChart.decrease}
            msg={cfrChart.msg}
            toolTip={cfrChart.toolTip}
            timeAgo={cfrChart.timeAgo}
            // defaultChangeMsg=""
          />
        </Col>
        <Col className="col-lg mb-1" key={2} md="6" sm="6">
          <SmallStats
            id={`small-stats-2`}
            variation="1"
            chartData={recoveredChart.datasets}
            chartLabels={recoveredChart.chartLabels}
            label={recoveredChart.label}
            value={recoveredChart.value}
            valueDiff={recoveredChart.valueDiff}
            percentage={recoveredChart.percentage}
            increase={recoveredChart.increase}
            isIncreaseBad={recoveredChart.isIncreaseBad}
            decrease={recoveredChart.decrease}
            msg={recoveredChart.msg}
            toolTip={recoveredChart.toolTip}
            timeAgo={recoveredChart.timeAgo}
          />
        </Col>
      </Row>

      {/*<div className="bg-info rounded p-2 mt-1 mb-3" style={{color:'white'}}>*/}
      {/*  <div>*/}
      {/*    <i className="fas fa-info-circle"></i> Note: We are currently making updates to include more data from official state & county data sources that have been made publicly available recently, so there may be delays in reporting during this transition period. Thank you for understanding.*/}
      {/*  </div>*/}
      {/*</div>*/}

      <Row>
        <Col lg="4" md="6" sm="12" className="mb-4">
          {sortedStateEntriesToShow ? (
            <Card small className="country-stats">
              <CardHeader className="border-bottom">
                <h6 className="m-0">
                  Cases by County{" "}
                  <a
                    href="javascript:void(0)"
                    onClick={() => setShowCityStats(!showCityStats)}
                  >
                    {showCityStats ? "(hide)" : "(unhide)"}
                  </a>
                </h6>
                {userCounty ? (
                  <div>
                    We detected you are in <u>{userCounty.title}</u>.
                  </div>
                ) : (
                  ""
                )}
                <div>Tap a state row to show its counties.</div>
                <div>Tap the blue chart icon to see historical data.</div>
                <div>Scroll down to see more.</div>
              </CardHeader>

              <Collapse open={showCityStats}>
                <div className="table-wrapper-scroll-y my-custom-scrollbar">
                  {sortedStateEntriesToShow.map(
                    (
                      [
                        state,
                        {
                          stateConfirmed,
                          stateDeaths,
                          stateNew,
                          sortedCountyEntries,
                        },
                      ],
                      idx
                    ) => (
                      <div key={idx}>
                        <div
                          className="card-header border-bottom table-state-header table-normal-row"
                          onClick={() => toggleShowState(state)}
                        >
                          <h6 className="m-0">
                            {isShowState(state) ? (
                              <i class="fas fa-angle-down"></i>
                            ) : (
                              <i class="fas fa-angle-right"></i>
                            )}
                            &nbsp;&nbsp;{state}&nbsp;&nbsp;
                            <a
                              className="table-chart-btn"
                              href="javascript:void(0)"
                            >
                              <i
                                class="fas fa-chart-line"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBigChartLocation(state, null);
                                }}
                              ></i>
                            </a>
                            <span style={{ float: "right", fontWeight: 400 }}>
                              {stateConfirmed.toLocaleString()}{" "}
                              <sup>
                                <small>
                                  {stateConfirmed == 1 ? "Case" : "Cases"}
                                </small>
                              </sup>
                              &nbsp;&nbsp;&nbsp;{stateDeaths.toLocaleString()}{" "}
                              <sup>
                                <small>
                                  {stateDeaths == 1 ? "Death" : "Deaths"}
                                </small>
                              </sup>
                            </span>
                          </h6>
                        </div>
                        <Collapse open={isShowState(state)}>
                          <CardBody className="p-0 border-bottom">
                            <table className="table mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th scope="col" className="border-0">
                                    {state == "LA" ? "Parish" : "County"}
                                  </th>
                                  <th scope="col" className="border-0">
                                    Confirmed
                                  </th>
                                  <th scope="col" className="border-0">
                                    Deaths
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedCountyEntries.map(
                                  (
                                    [
                                      county,
                                      {
                                        countyConfirmed,
                                        countyDeaths,
                                        countyNew,
                                      },
                                    ],
                                    idx
                                  ) => (
                                    // <div key={idx}>
                                    <tr
                                      key={idx}
                                      className={`${
                                        userCounty &&
                                        userCounty.county == county
                                          ? "table-highlighted-row"
                                          : ""
                                      }`}
                                    >
                                      <td>
                                        {county}&nbsp;&nbsp;
                                        <a
                                          className="table-chart-btn"
                                          href="javascript:void(0)"
                                        >
                                          <i
                                            class="fas fa-chart-line"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setBigChartLocation(
                                                state,
                                                county
                                              );
                                            }}
                                          ></i>
                                        </a>
                                      </td>
                                      <td>
                                        {countyConfirmed.toLocaleString()}
                                      </td>
                                      <td>
                                        {countyDeaths
                                          ? countyDeaths.toLocaleString()
                                          : 0}
                                      </td>
                                    </tr>
                                    // </div>
                                  )
                                )}
                              </tbody>
                            </table>
                          </CardBody>
                        </Collapse>
                      </div>
                    )
                  )}
                </div>
              </Collapse>
            </Card>
          ) : (
            ""
          )}

          {/* <Card small className="country-stats">
            <CardHeader className="border-bottom">
              <h6 className="m-0">Cases/Deaths by Location <a href="javascript:void(0)" onClick={() => setShowCityStats(!showCityStats)}>{showCityStats ? '(hide)' : '(unhide)'}</a></h6>
              <div className="block-handle" />
            </CardHeader>

            <Collapse open={showCityStats}>
              <CardBody className="p-0">
                <div className="table-wrapper-scroll-y my-custom-scrollbar">
                  <table className="table m-0">
                    <tbody>
                      {
                        sortedStateEntries.map(([state, { stateConfirmed, stateDeaths, stateNew, sortedCountyEntries }], idx) => (
                          (
                            <tr key={idx}>
                              <td>
                                <div>
                                  <u>
                                    {state}
                                  </u>
                                </div>
                                <div>
                                  {sortedCountyEntries.map(([county, { countyConfirmed, countyDeaths, countyNew }], idx) => (
                                    <div key={idx}>
                                      {county}: <span><span className="confirmed-style">{countyConfirmed}</span> <span className="deaths-style">{countyDeaths || 0}</span></span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                              <td className="text-right"><span className="confirmed-style">{stateConfirmed}</span> <span className="deaths-style">{stateDeaths || 0}</span></td>
                            </tr>
                          )
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Collapse>
          </Card> */}
        </Col>

        {/* Users Overview */}
        <Col
          lg="8"
          md="12"
          sm="12"
          className="users-overview-spacing big-chart-container"
        >
          {ts ? (
            <UsersOverview
              // confirmed={confirmedHistoricalList}
              // deaths={confirmedHistoricalList}
              // recovered={recoveredHistoricalList}
              ts={ts}
              selectedState={selectedState}
              selectedCounty={selectedCounty}
              setSelectedState={setSelectedState}
              setSelectedCounty={setSelectedCounty}
            />
          ) : (
            ""
          )}
        </Col>

        {/* New Draft */}
        {/* <Col lg="4" md="6" sm="12" className="mb-4">
        <NewDraft />
      </Col> */}

        {/* Discussions */}
        {/* <Col lg="5" md="12" sm="12" className="mb-4">
        <Discussions />
      </Col> */}

        {/* Top Referrals */}
        {/* <Col lg="3" md="12" sm="12" className="mb-4">
        <TopReferrals />
      </Col> */}
      </Row>
      <Row>
        <Col lg="12" md="12" sm="12" className="mb-4">
          <div className="text-center">
            <div>
              Data sources:{" "}
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/index.html"
                target="_blank"
              >
                CDC
              </a>
              ,{" "}
              <a
                href="https://www.who.int/emergencies/diseases/novel-coronavirus-2019"
                target="_blank"
              >
                WHO
              </a>
              ,{" "}
              <a
                href="https://www.nytimes.com/interactive/2020/us/coronavirus-us-cases.html"
                target="_blank"
              >
                The New York Times
              </a>
              ,{" "}
              <a href="https://www.jhu.edu/" target="_blank">
                JHU
              </a>
              ,{" "}
              <a href="https://coronadatascraper.com/" target="_blank">
                Corona Data Scraper
              </a>
              , and official state and county health agencies.
            </div>
            <div>
              Please{" "}
              <a href="mailto:usacoronavirustracker@gmail.com" target="_blank">
                contact us
              </a>{" "}
              if you have any suggestions or feedback.
            </div>
            <div>
              Big thanks to{" "}
              <a href="https://www.netlify.com/" target="_blank">
                Netlify
              </a>{" "}
              and{" "}
              <a href="https://www.mapbox.com/" target="_blank">
                Mapbox
              </a>{" "}
              for supporting us during this outbreak.
            </div>
            <Tooltip
              title="Disclaimer: All information on this website (infection2020.com) is published in good faith and for general information purposes only. The website relies upon publicly available data from multiple sources, which do not always agree. Infection2020.com does not make any warranties about the completeness, reliability, or accuracy of the information, and is not responsible for any consequences resulting from the use of this information."
              position="top"
              trigger="click"
            >
              <small>
                <a href="javascript:void(0)">Disclaimer</a>
              </small>
            </Tooltip>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg="12" md="12" sm="12" className="mb-4">
          <div className="text-center">
            <h5>Additional Resources</h5>
            <div>
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/if-you-are-sick/steps-when-sick.html"
                target="_blank"
              >
                What To Do If You Are Sick (CDC.gov)
              </a>
            </div>
            <div>
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/symptoms-testing/symptoms.html"
                target="_blank"
              >
                What Are The Symptoms? (CDC.gov)
              </a>
            </div>
            <div>
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/prepare/transmission.html"
                target="_blank"
              >
                How It Spreads (CDC.gov)
              </a>
            </div>
            <div>
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/prepare/prevention.html"
                target="_blank"
              >
                How to Protect Yourself (CDC.gov)
              </a>
            </div>
            <div>
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/specific-groups/high-risk-complications.html"
                target="_blank"
              >
                Are You At High Risk? (CDC.gov)
              </a>
            </div>
            <div>
              <a
                href="https://www.cdc.gov/coronavirus/2019-ncov/travelers/travel-in-the-us.html"
                target="_blank"
              >
                Can I still travel within the US? (CDC.gov)
              </a>
            </div>
            <br />
          </div>
        </Col>
      </Row>
      <br />
      <br />
      <br />
      <br />
    </Container>
  );
};

BlogOverview.propTypes = {
  /**
   * The small stats dataset.
   */
  smallStats: PropTypes.array,
};

function getPercentDiff(oldValue, newValue) {
  if (oldValue > 0) {
    return (newValue - oldValue) / oldValue;
  } else {
    return 1;
  }
}

function deepClone(objOrList) {
  return JSON.parse(JSON.stringify(objOrList));
}

function getBigChartValuesToShow(historicalList) {
  const period = 21; //days
  const latestHistoricalEntries = historicalList.slice(
    historicalList.length - period
  );
  return {
    labels: latestHistoricalEntries.map((entry) => entry[0]),
    data: latestHistoricalEntries.map((entry) => entry[1].totalCount),
  };
}

// 3/24/20
function getTodayDateString() {
  const today = new Date();
  return `${today.getMonth() + 1}/${today.getDate()}/${today
    .getFullYear()
    .toString()
    .slice(-2)}`;
}

function isObjEmpty(obj) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

function getScrapedDeltaInfo(currentCount, increase) {
  const percentDiff = getPercentDiff(currentCount - increase, currentCount);
  const percentToShow = Math.round(percentDiff * 100) + "%";
  return {
    percentToShow,
    isIncrease: increase >= 0,
  };
}

function createSmallChart(
  chartValueType,
  chartProps,
  dataStyle,
  chartValue,
  valueDiff,
  hoursAgo,
  data
) {
  const output = {
    ...chartProps,
  };
  if (!data) return output;
  output.value = chartValue;

  // if (ts) {
  //   const tsToShow = ts.slice(-7) // show last 7 days
  output.chartLabels = data.map(() => null);
  output.datasets = [
    {
      ...dataStyle,
      // data: tsToShow.map(entry => entry[chartValueType]),
      data,
    },
  ];
  // }

  if (valueDiff != null) {
    const { percentToShow, isIncrease } = getScrapedDeltaInfo(
      chartValue,
      valueDiff
    );
    output.valueDiff = valueDiff;
    output.percentage = percentToShow;
    output.increase = isIncrease;
    output.timeAgo = hoursAgo;
  }

  return output;
}

function createCFRChart(newValue, diff, ts) {
  const chartProps = {
    label: "Case Fatality Rate",
    isIncreaseBad: true,
    attrs: { md: "6", sm: "6" },
    toolTip:
      "Case Fatality Rate (CFR) is the percentage of people diagnosed with COVID-19 that die from the disease. CFR = (Number of confirmed deaths / Number of confirmed cases) * 100",
  };

  const dataStyle = {
    label: "Today",
    fill: "start",
    borderWidth: 1.5,
    backgroundColor: "rgba(255,0,0,0.1)",
    borderColor: "rgb(255,0,0,0.7)",
  };

  if (ts) {
    ts = ts.slice(-7);
    ts = ts.map((e) => {
      const totalConfirmed = e["totalConfirmed"];
      const totalDeaths = e["totalDeaths"];
      return calcCFR(totalConfirmed, totalDeaths);
    });
  }

  const chartValueType = "totalCaseFatalityRate";
  // return createSmallChart(chartValueType, chartProps, dataStyle, currCFR, valueDiff, null, ts)
  return createSmallChart(
    chartValueType,
    chartProps,
    dataStyle,
    newValue,
    null,
    null,
    ts
  );
}

function createConfirmedChart(newValue, diff, ts) {
  const chartProps = {
    label: "Cases",
    isIncreaseBad: true,
    attrs: { md: "6", sm: "6" },
  };

  const dataStyle = {
    label: "Today",
    fill: "start",
    borderWidth: 1.5,
    backgroundColor: "rgba(255, 165, 0, 0.1)",
    borderColor: "rgb(255, 165, 0, 0.7)",
  };

  const chartValueType = "totalConfirmed";
  return createSmallChart(
    chartValueType,
    chartProps,
    dataStyle,
    newValue,
    diff && diff[chartValueType],
    diff && diff.hoursAgo,
    ts && ts.map((e) => e[chartValueType]).slice(-7)
  );
}

function createDeathsChart(newValue, diff, ts) {
  const chartProps = {
    label: "Deaths",
    isIncreaseBad: true,
    attrs: { md: "6", sm: "6" },
  };

  const dataStyle = {
    label: "Today",
    fill: "start",
    borderWidth: 1.5,
    backgroundColor: "rgba(255,0,0,0.1)",
    borderColor: "rgb(255,0,0,0.7)",
  };

  const chartValueType = "totalDeaths";
  return createSmallChart(
    chartValueType,
    chartProps,
    dataStyle,
    newValue,
    diff && diff[chartValueType],
    diff && diff.hoursAgo,
    ts && ts.map((e) => e[chartValueType]).slice(-7)
  );
}

function createRecoveredChart(newValue, diff, ts) {
  const chartProps = {
    label: "Recovered",
    toolTip:
      "Data on recovered cases is limited. The actual number of recovered is likely substantially higher.",
    isIncreaseBad: false,
    attrs: { md: "6", sm: "6" },
  };

  const dataStyle = {
    label: "Today",
    fill: "start",
    borderWidth: 1.5,
    backgroundColor: "rgba(23,198,113,0.1)",
    borderColor: "rgb(23,198,113,0.7)",
  };

  const chartValueType = "totalRecovered";
  return createSmallChart(
    chartValueType,
    chartProps,
    dataStyle,
    newValue,
    diff && diff[chartValueType],
    diff && diff.hoursAgo,
    ts && ts.map((e) => e[chartValueType]).slice(-7)
  );
}

BlogOverview.defaultProps = {};

export default BlogOverview;
